import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import {
    LoggedWorkByIdData,
    LoggedWorkByIdResponse,
    UpdateLoggedWorkAbsenceRequest,
    UpdateLoggedWorkAdditionRequest,
    UpdateLoggedWorkHourRequest,
    UpdateLoggedWorkPieceworkRequest,
} from '@core/app/types/logworkApiType'

export const loggedWorkApi = createApi({
    reducerPath: 'loggedWorkApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.ENV === 'local' ? `${process.env.API_URL}/api` : `${window.location.origin}/api`,
        prepareHeaders: (headers) => {
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)

            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    tagTypes: ['LoggedWork', 'LoggedWorkById'],
    endpoints: (builder) => ({
        getData: builder.query({
            query: (arg) => {
                const {
                    fromDate,
                    toDate,
                    scopes,
                    workJobs,
                    workSubProjects,
                    workTrips,
                    workTripIds,
                    workAttachments,
                    workNotes,
                    workStatus,
                    workArticleIds,
                } = arg
                return {
                    url: '/employee/calendar/v2',
                    method: 'GET',
                    params: {
                        fromDate,
                        toDate,
                        scopes,
                        workJobs,
                        workSubProjects,
                        workTrips,
                        workTripIds,
                        workAttachments,
                        workNotes,
                        workStatus,
                        workArticleIds,
                    },
                }
            },
            transformResponse: (response: any) => {
                return response.data
            },
            providesTags: ['LoggedWork'],
        }),
        downloadData: builder.query({
            query: (arg) => {
                const {
                    fromDate,
                    toDate,
                    scopes,
                    workJobs,
                    workSubProjects,
                    workTrips,
                    workTripIds,
                    workAttachements,
                    workNotes,
                    workStatus,
                    workArticleIds,
                    format,
                    ids,
                } = arg

                return {
                    url: '/employee/report/logged-work',
                    method: 'GET',
                    params: {
                        fromDate,
                        toDate,
                        scopes,
                        workJobs,
                        workSubProjects,
                        workTrips,
                        workTripIds,
                        workAttachements,
                        workNotes,
                        workStatus,
                        workArticleIds,
                        format,
                        ids,
                    },
                }
            },
        }),
        deleteWork: builder.mutation({
            query: (arg) => {
                const { ids } = arg
                return {
                    url: '/employee/work/delete',
                    method: 'DELETE',
                    body: { ids },
                }
            },
            transformResponse: (response: any) => {
                return response.data
            },
            invalidatesTags: ['LoggedWork'],
        }),
        updateWorkHour: builder.mutation<void, UpdateLoggedWorkHourRequest>({
            query: (body) => ({
                url: `/employee/work/edit/${body.workHourId}/hour`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['LoggedWork', 'LoggedWorkById'],
        }),
        updatePiecework: builder.mutation<void, UpdateLoggedWorkPieceworkRequest>({
            query: (body) => ({
                url: `/employee/work/edit/${body.workHourId}/piecework`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['LoggedWork', 'LoggedWorkById'],
        }),
        updateAddition: builder.mutation<void, UpdateLoggedWorkAdditionRequest>({
            query: (body) => ({
                url: `/employee/work/edit/${body.workHourId}/addition`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['LoggedWork', 'LoggedWorkById'],
        }),
        updateAbsence: builder.mutation<void, UpdateLoggedWorkAbsenceRequest>({
            query: (body) => ({
                url: `/employee/work/edit/${body.workHourId}/absence`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['LoggedWork', 'LoggedWorkById'],
        }),
        getLoggedWorkByIdLoggedWorkPage: builder.query<LoggedWorkByIdData, number>({
            query: (id) => ({
                url: `/employee/work/${id}`,
                method: 'GET',
            }),
            transformResponse: (response: LoggedWorkByIdResponse) => response.data,
            providesTags: ['LoggedWorkById'],
        }),
        uploadFileToLoggedWork: builder.mutation({
            query: (body) => ({
                url: `/employee/work/${body.id}/upload`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['LoggedWorkById', 'LoggedWork'],
        }),
        deleteFileFromLoggedWork: builder.mutation<
            void,
            { fileId: number; workHourId: number; dates: { start: string; end: string } }
        >({
            query: (body) => ({
                url: `/employee/work/${body.workHourId}/delete/${body.fileId}`,
                method: 'DELETE',
                body,
            }),
            invalidatesTags: ['LoggedWorkById', 'LoggedWork'],
        }),
    }),
})

export const {
    useLazyGetDataQuery,
    useLazyDownloadDataQuery,
    useDeleteWorkMutation,
    useUpdateWorkHourMutation,
    useUpdatePieceworkMutation,
    useUpdateAdditionMutation,
    useUpdateAbsenceMutation,
    useGetLoggedWorkByIdLoggedWorkPageQuery,
    useUploadFileToLoggedWorkMutation,
    useDeleteFileFromLoggedWorkMutation,
} = loggedWorkApi
